import { Injectable } from '@angular/core';
import {from, Observable, of, throwError} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {classToPlain, plainToClass} from 'class-transformer';
import {db} from "../../dexie/db";

import { SOPutRelationshipAttributePermissionRule } from './so-put-relationship-attribute-permission-rule';
import { SOGetRelationshipAttributePermissionRule } from './so-get-relationship-attribute-permission-rule';
import { SOPostRelationshipAttributePermissionRule } from './so-post-relationship-attribute-permission-rule';
import { SODeleteRelationshipAttributePermissionRule } from './so-delete-relationship-attribute-permission-rule';
import {SORespRelationshipAttributePermissionRule} from './so-resp-relationship-attribute-permission-rule';
import {SOPluralRelationshipAttributePermissionRule} from './so-plural-relationship-attribute-permission-rule';

import {MMEntityService} from 'lionheartland';
import {EntityCoreProtocol} from 'lionheartland';
import {MMEntityServiceBase} from 'lionheartland';
import {HttpHeaders} from "@angular/common/http";

export class EORelationshipAttributePermissionRuleService extends MMEntityServiceBase implements MMEntityService {

  constructor(
    public entityCore: EntityCoreProtocol,
  ) {
    super('relationship_attribute_permission_rule', entityCore);
  }

  /** GET RelationshipAttributePermissionRule by id. Will 404 if id not found */
  get(request: SOGetRelationshipAttributePermissionRule): Observable<SOPluralRelationshipAttributePermissionRule> {
    return this.httpClient.get<SOPluralRelationshipAttributePermissionRule>(this.makeGetUri(request), this.httpOptions).pipe(
      map(resp => plainToClass(SOPluralRelationshipAttributePermissionRule, resp)),
      tap(async (response: SOPluralRelationshipAttributePermissionRule) => {
        this.log(`fetched RelationshipAttributePermissionRule`);
        // After a successful server response, update IndexedDB
        const activities = response.toRelationshipAttributePermissionRules(); // Convert your response to an array of RelationshipAttributePermissionRule instances
        const promises = activities.map(relationshipAttributePermissionRule => db.relationshipAttributePermissionRules.put(relationshipAttributePermissionRule));
        await Promise.all(promises); // Wait for all the IndexedDB update operations to complete
      }),
      catchError(this.handleError<SOPluralRelationshipAttributePermissionRule>(`get RelationshipAttributePermissionRule`))
    );
  }

    /** GETCACHE for initial display with CacheQueryProtocol */
    getCache(cacheQuery: any): Observable<SOPluralRelationshipAttributePermissionRule> {
        console.log("GETCACHE", cacheQuery);

        if (cacheQuery.filterKey === 'relationshipAttributePermissionRule' && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on parent and organization")
            return from(db.relationshipAttributePermissionRules.where('parentUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }
        else if (cacheQuery.filterKey + "s" == "relationshipAttribute" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.relationshipAttributePermissionRules.where('relationshipAttributeUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "relationshipAttributePermission" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.relationshipAttributePermissionRules.where('relationshipAttributePermissionUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "project" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.relationshipAttributePermissionRules.where('projectUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterKey + "s" == "organization" && cacheQuery.filterValue && cacheQuery.filterOrganization)
        {
            console.log("cache filtering based on relationship and organization")
            return from(db.relationshipAttributePermissionRules.where('organizationUid').equals(cacheQuery.filterValue).and(item => item.organizationUid === cacheQuery.filterOrganization.uid).toArray()).pipe(
                map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                catchError(error => this.handleQueryError(error))
            );
        }        else if (cacheQuery.filterOrganization || cacheQuery.organization.uid)
        {
            console.log("tripped organization for cache query")
            if ( cacheQuery?.filterOrganization != null )
            {
                console.log("cache filtering based on organization CQP", cacheQuery)
                return from(db.relationshipAttributePermissionRules.where('organizationUid').equals(cacheQuery.filterOrganization).toArray()).pipe(
                    map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else if ( cacheQuery?.organization?.uid != null )
            {
                console.log("UID:", cacheQuery.organization.uid)
                console.log("cache filtering based on organization SOGet")
                return from(db.relationshipAttributePermissionRules.where('organizationUid').equals(cacheQuery.organization.uid).toArray()).pipe(
                    map(relationshipAttributePermissionRulesArray => this.processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray)),
                    catchError(error => this.handleQueryError(error))
                );
            }
            else
            {
                console.log("null organization")
            }
        }
        else
        {
            console.log("cache no filter")
            return from(db.relationshipAttributePermissionRules.toArray()).pipe(
                map((relationshipAttributePermissionRulesArray) => {
                    // Convert the array of plain objects to an array of SORespRelationshipAttributePermissionRule instances
                    const sorRelationshipAttributePermissionRules = relationshipAttributePermissionRulesArray.map(req => plainToClass(SORespRelationshipAttributePermissionRule, req));
                    // Create and return a new instance of SOPluralRelationshipAttributePermissionRule
                    const pluralRelationshipAttributePermissionRule = new SOPluralRelationshipAttributePermissionRule();
                    pluralRelationshipAttributePermissionRule.objects = sorRelationshipAttributePermissionRules;
                    pluralRelationshipAttributePermissionRule.nextOffset = null; // Assuming there is no pagination in cache
                    return pluralRelationshipAttributePermissionRule;
                }),
                catchError(error => this.handleQueryError(error))
            );
        }
    }

    private processRelationshipAttributePermissionRules(relationshipAttributePermissionRulesArray) {
        const sorRelationshipAttributePermissionRules = relationshipAttributePermissionRulesArray.map(req => plainToClass(SORespRelationshipAttributePermissionRule, req));
        const pluralRelationshipAttributePermissionRule = new SOPluralRelationshipAttributePermissionRule();
        pluralRelationshipAttributePermissionRule.objects = sorRelationshipAttributePermissionRules;
        pluralRelationshipAttributePermissionRule.nextOffset = null;
        return pluralRelationshipAttributePermissionRule;
    }

    private handleQueryError(error) {
        console.error('Error in query:', error);
        return throwError(error); // Return an observable error
    }

post(request: SOPostRelationshipAttributePermissionRule): Observable<SORespRelationshipAttributePermissionRule> {
  return this.httpClient.post<SORespRelationshipAttributePermissionRule>(this.coreUrl + this.route, request, this.httpOptions).pipe(
    map(resp => plainToClass(SORespRelationshipAttributePermissionRule, resp)),

    tap(async (response: SORespRelationshipAttributePermissionRule) => {
      this.log(`added RelationshipAttributePermissionRule w/ id=${response.uid}`);
      // After a successful server response, update IndexedDB
      console.log("here", response);
      console.log("here request", request);
      await db.relationshipAttributePermissionRules.add(response.toRelationshipAttributePermissionRule());
    }),
    catchError(this.handleError<SORespRelationshipAttributePermissionRule>('RelationshipAttributePermissionRule post'))
  );
}


  put(request: SOPutRelationshipAttributePermissionRule): Observable<SORespRelationshipAttributePermissionRule> {
    const uid = request.uid;
    const uri = `${this.coreUrl}${this.route}/${uid}`;
    return this.httpClient.put<SORespRelationshipAttributePermissionRule>(uri, request, this.httpOptions).pipe(
      map(resp => plainToClass(SORespRelationshipAttributePermissionRule, resp)),
      tap(async (response: SORespRelationshipAttributePermissionRule) => {
        this.log(`edited RelationshipAttributePermissionRule w/ uid=${uid}`);
        // After a successful server response, update IndexedDB
        await db.relationshipAttributePermissionRules.put(response.toRelationshipAttributePermissionRule());
      }),
      catchError(this.handleError<SORespRelationshipAttributePermissionRule>('RelationshipAttributePermissionRule put'))
    );
  }


  delete(request: SODeleteRelationshipAttributePermissionRule): Observable<SORespRelationshipAttributePermissionRule> {
    return this.httpClient.delete<SORespRelationshipAttributePermissionRule>(this.makeDeleteUri(request), this.httpOptions).pipe(
      tap(async _ => {
        this.log(`deleted RelationshipAttributePermissionRule uid=${request.uid}`);
        // After a successful server response, update IndexedDB
        await db.relationshipAttributePermissionRules.delete(request.uid);
      }),
      catchError(this.handleError<SORespRelationshipAttributePermissionRule>('RelationshipAttributePermissionRule delete'))
    );
  }


}
